<template>
  <div class="rmr-page-shell mid-page">
    <rmr-page-hero
      title="Données"
      title-accent="Importées"
      subtitle="Métriques de marque agrégées depuis les fichiers Excel importés"
      :icon="icons.mdiDatabase"
    >
      <template #actions>
        <rmr-icon-button
          :icon="icons.mdiHelpCircleOutline"
          variant="edit"
          size="sm"
          tooltip="Guide d'utilisation"
          @click="helpDialog = true"
        />
        <rmr-action-button
          label="Process Data"
          :icon="icons.mdiCog"
          variant="primary"
          color="#16A34A"
          size="sm"
          :loading="processing"
          :disabled="!hasExcelFiles"
          @click="processData"
        />
        <rmr-action-button
          label="Vider les rapports"
          :icon="icons.mdiDeleteSweep"
          variant="primary"
          color="#DC2626"
          size="sm"
          :disabled="brandReports.length === 0"
          :loading="loading"
          @click="confirmClearReports"
        />
      </template>
    </rmr-page-hero>

    <section class="rmr-page-shell__content mid-content">
      <imported-data-table-card
        :headers="headers"
        :items="filteredReports"
        :metrics="tableMetrics"
        :filter-brand="filterBrand"
        :loading="loading"
        :format-number="formatNumber"
        :format-metric-display="formatMetricDisplay"
        :get-imported-value="getImportedValue"
        :get-override-value="getOverrideValue"
        :refresh-icon="icons.mdiRefresh"
        :edit-icon="icons.mdiPencil"
        :database-icon="icons.mdiDatabase"
        @update:filter-brand="filterBrand = $event"
        @refresh="loadData"
        @edit-overrides="openOverridesDialog"
      />

      <rmr-modal
        v-model="helpDialog"
        variant="form"
        :icon="icons.mdiHelpCircleOutline"
        eyebrow="Guide"
        title="Données importées et valeurs manuelles"
        subtitle="Comment fonctionnent les données importées, les valeurs manuelles et la suppression."
        max-width="660"
      >
        <template #body>
          <v-card-text class="pa-6 mid-help">
            <div class="mid-help__section">
              <div class="mid-help__title">Données importées</div>
              <ul class="mid-help__list">
                <li>Les métriques sont lues depuis les fichiers Excel importés dans <strong>Data Import</strong>.</li>
                <li>Cliquez sur <strong>Process Data</strong> pour déclencher l'import et mettre à jour les valeurs.</li>
                <li>Chaque traitement remet à zéro les valeurs importées puis les recharge depuis l'Excel.</li>
              </ul>
            </div>

            <div class="mid-help__section">
              <div class="mid-help__title">Valeurs manuelles</div>
              <ul class="mid-help__list">
                <li>Cliquez sur le bouton <strong>Modifier</strong> d'une marque pour saisir des valeurs manuelles.</li>
                <li>Une valeur manuelle remplace la valeur importée correspondante dans les présentations et publications.</li>
                <li>Les valeurs importées ne sont jamais supprimées par une saisie manuelle.</li>
                <li>Un nouveau traitement Excel conserve les valeurs manuelles existantes.</li>
              </ul>
            </div>

            <div class="mid-help__section mid-help__section--warning">
              <div class="mid-help__title mid-help__title--warning">Attention — Vider les rapports</div>
              <ul class="mid-help__list">
                <li><strong>Vider les rapports supprime toutes les données</strong>, y compris les valeurs manuelles saisies.</li>
                <li>Cette action est irréversible. Réimportez et retraitez les fichiers Excel pour rétablir les données.</li>
              </ul>
            </div>
          </v-card-text>
        </template>
      </rmr-modal>

      <metric-overrides-modal
        v-model="overridesDialog"
        :selected-report="selectedReport"
        :editable-metrics="editableMetrics"
        :override-form="overrideForm"
        :saving-overrides="savingOverrides"
        :get-imported-value="getImportedValue"
        :get-effective-value-from-form="getEffectiveValueFromForm"
        :format-metric-display="formatMetricDisplay"
        :icon="icons.mdiDatabase"
        :trash-icon="icons.mdiTrashCan"
        @update-field="updateOverrideField"
        @clear-field="clearOverrideField"
        @clear-all="clearAllOverrides"
        @close="closeOverridesDialog"
        @save="saveOverrides"
      />

      <rmr-confirm-dialog
        v-model="clearDialog"
        title="Vider les rapports"
        message="Cette action est irréversible et supprimera toutes les données y compris les valeurs manuelles."
        confirm-label="Tout supprimer"
        cancel-label="Annuler"
        danger
        @confirm="clearAllReports"
        @cancel="clearDialog = false"
      />
    </section>
  </div>
</template>

<script>
import {
  mdiRefresh,
  mdiDeleteSweep,
  mdiCog,
  mdiDatabase,
  mdiPencil,
  mdiHelpCircleOutline,
  mdiTrashCan,
} from "@mdi/js";
import * as XLSX from "xlsx";
import axios from "@axios";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import ImportedDataTableCard from "@/components/marketing/imported-data/ImportedDataTableCard.vue";
import MetricOverridesModal from "@/components/marketing/imported-data/MetricOverridesModal.vue";
import RmrPageHero from "@/components/ui/layout/RmrPageHero.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";

export default {
  name: "ViewImportedData",
  components: {
    ImportedDataTableCard,
    MetricOverridesModal,
    RmrActionButton,
    RmrConfirmDialog,
    RmrIconButton,
    RmrPageHero,
    RmrModal,
  },
  data() {
    return {
      loading: false,
      processing: false,
      helpDialog: false,
      icons: {
        mdiRefresh,
        mdiDeleteSweep,
        mdiCog,
        mdiDatabase,
        mdiPencil,
        mdiHelpCircleOutline,
        mdiTrashCan,
      },
      editableMetrics: [
        { key: "audience_brand", label: "Audience Brand", importedField: "audience_brand", overrideField: "audience_brand_override" },
        { key: "audience_print_readers", label: "Print Readers", importedField: "audience_print_readers", overrideField: "audience_print_readers_override" },
        { key: "audience_digital", label: "Digital", importedField: "audience_digital", overrideField: "audience_digital_override" },
        { key: "diffusion_print", label: "Diffusion", importedField: "diffusion_print", overrideField: "diffusion_print_override" },
        { key: "social_data", label: "Social", importedField: "social_data", overrideField: "social_data_override" },
        { key: "audio_data", label: "Audio", importedField: "audio_data", overrideField: "audio_data_override" },
        { key: "newsletter_data", label: "Newsletter", importedField: "newsletter_data", overrideField: "newsletter_data_override" },
      ],
      excelData: {
        audienceBrand: null,
        audiencePrint: null,
        audienceDigit: null,
        diffPrint: null,
        social: null,
        audio: null,
        newsletter: null,
      },
      columnMapping: {
        audienceBrand: {
          headerRow: 0,
          dataStartRow: 1,
          labelColumn: 0,
          valueColumns: [2],
        },
        audiencePrint: {
          headerRow: 0,
          dataStartRow: 1,
          labelColumn: 1,
          valueColumns: [2],
        },
        audienceDigit: {
          headerRow: 0,
          dataStartRow: 2,
          labelColumn: 0,
          valueColumns: [1, 2, 3],
        },
        diffPrint: {
          headerRow: -1,
          dataStartRow: 0,
          labelColumn: 0,
          valueColumns: [1],
        },
        social: {
          headerRow: 0,
          dataStartRow: 1,
          labelColumn: 2,
          valueColumns: [5],
        },
        audio: {
          headerRow: 0,
          dataStartRow: 1,
          labelColumn: 0,
          valueColumns: [2],
        },
        newsletter: {
          headerRow: -1,
          dataStartRow: 0,
          labelColumn: 0,
          valueColumns: [1],
        },
      },
      headers: [
        { text: "Marque", value: "brand_name", width: "180px" },
        { text: "Audience Brand", value: "audience_brand", align: "end", width: "120px" },
        { text: "Print Readers", value: "audience_print_readers", align: "end", width: "110px" },
        { text: "Digital", value: "audience_digital", align: "end", width: "110px" },
        { text: "Diffusion", value: "diffusion_print", align: "end", width: "100px" },
        { text: "Social", value: "social_data", align: "end", width: "100px" },
        { text: "Audio", value: "audio_data", align: "end", width: "100px" },
        { text: "Newsletter", value: "newsletter_data", align: "end", width: "100px" },
        { text: "Actions", value: "actions", sortable: false, align: "center", width: "52px" },
      ],
      brandReports: [],
      filterBrand: "",
      clearDialog: false,
      overridesDialog: false,
      selectedReport: null,
      savingOverrides: false,
      overrideForm: {},
    };
  },
  computed: {
    filteredReports() {
      var self = this;
      if (!self.filterBrand) {
        return self.brandReports;
      }
      var filter = self.filterBrand.toLowerCase();
      return self.brandReports.filter(function(report) {
        return report.brand_name &&
          report.brand_name.toLowerCase().indexOf(filter) !== -1;
      });
    },
    hasExcelFiles() {
      return Object.values(this.excelData).some(function(d) {
        return d !== null;
      });
    },
    tableMetrics() {
      return this.editableMetrics.map(function(metric) {
        return {
          key: metric.key,
          importedField: metric.importedField,
          overrideField: metric.overrideField,
          monthField: metric.importedField === "audience_digital" ? "digital_month_data" : null,
        };
      });
    },
  },
  mounted() {
    this.loadData();
    this.checkExcelFiles();
  },
  methods: {
    showSnackbar(text, color = "success", timeout = 4000) {
      this.$store.dispatch("app/setSnackBar", { show: true, text, color, timeout });
    },
    async loadData() {
      this.loading = true;
      try {
        var response = await axios.get("/api/marketing/brand-reports");
        this.brandReports = response.data.items || [];
      } catch (error) {
        console.error("Error loading data:", error);
        this.showSnackbar("Erreur lors du chargement des données", "error");
      } finally {
        this.loading = false;
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined) return "";
      return new Intl.NumberFormat("fr-FR").format(value);
    },
    getImportedValue(report, field) {
      return report && report.imported_values ? report.imported_values[field] ?? null : null;
    },
    getOverrideValue(report, field) {
      return report && report.override_values ? report.override_values[field] ?? null : null;
    },
    getEffectiveValue(report, importedField, overrideField) {
      var overrideValue = this.getOverrideValue(report, overrideField);
      if (overrideValue !== null && overrideValue !== undefined && overrideValue !== "") {
        return overrideValue;
      }
      return this.getImportedValue(report, importedField);
    },
    formatMetricDisplay(value, metric) {
      if (value === null || value === undefined || value === "") return "—";
      return this.formatNumber(value);
    },
    openOverridesDialog(report) {
      var form = {};
      this.editableMetrics.forEach((metric) => {
        var value = this.getOverrideValue(report, metric.overrideField);
        form[metric.overrideField] = value !== null && value !== undefined ? String(value) : "";
      });
      this.selectedReport = report;
      this.overrideForm = form;
      this.overridesDialog = true;
    },
    closeOverridesDialog() {
      this.overridesDialog = false;
      this.selectedReport = null;
      this.overrideForm = {};
    },
    clearOverrideField(fieldName) {
      this.$set(this.overrideForm, fieldName, "");
    },
    updateOverrideField(payload) {
      this.$set(this.overrideForm, payload.field, payload.value);
    },
    clearAllOverrides() {
      this.editableMetrics.forEach((metric) => {
        this.$set(this.overrideForm, metric.overrideField, "");
      });
    },
    getEffectiveValueFromForm(metric) {
      var current = this.overrideForm[metric.overrideField];
      if (current !== null && current !== undefined && String(current).trim() !== "") {
        var parsed = Number(current);
        return Number.isNaN(parsed) ? null : parsed;
      }
      return this.getImportedValue(this.selectedReport, metric.importedField);
    },
    async saveOverrides() {
      if (!this.selectedReport) return;
      this.savingOverrides = true;
      try {
        var payload = {};
        this.editableMetrics.forEach((metric) => {
          var rawValue = this.overrideForm[metric.overrideField];
          if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") {
            payload[metric.overrideField] = null;
            return;
          }
          var parsed = Number(rawValue);
          payload[metric.overrideField] = Number.isNaN(parsed) ? null : parsed;
        });
        await axios.patch(
          "/api/marketing/brand-reports/" + this.selectedReport.brand_id + "/overrides",
          payload
        );
        var brandName = this.selectedReport.brand_name;
        await this.loadData();
        this.closeOverridesDialog();
        this.showSnackbar("Valeurs manuelles enregistrées pour " + brandName);
      } catch (error) {
        console.error("Error saving overrides:", error);
        this.showSnackbar("Erreur lors de l'enregistrement", "error");
      } finally {
        this.savingOverrides = false;
      }
    },
    confirmClearReports() {
      this.clearDialog = true;
    },
    async clearAllReports() {
      this.loading = true;
      try {
        await axios.delete("/api/marketing/brand-reports");
        this.showSnackbar("Tous les rapports ont été supprimés");
        this.brandReports = [];
      } catch (error) {
        console.error("Error clearing reports:", error);
        this.showSnackbar("Erreur lors de la suppression", "error");
      } finally {
        this.loading = false;
        this.clearDialog = false;
      }
    },
    async checkExcelFiles() {
      var self = this;
      try {
        var response = await axios.get("/api/marketing/excel-files");
        var files = response.data.files || [];
        var existingFiles = files.filter(function(f) { return f.exists; });
        for (var i = 0; i < existingFiles.length; i++) {
          var file = existingFiles[i];
          await self.loadFileFromServer(file.source_key);
        }
      } catch (error) {
        console.error("Error checking excel files:", error);
      }
    },
    async loadFileFromServer(sourceKey) {
      var self = this;
      try {
        var response = await axios.get("/api/marketing/excel-files/" + sourceKey, {
          responseType: "arraybuffer",
        });
        var data = new Uint8Array(response.data);
        var workbook = XLSX.read(data, { type: "array" });
        var firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        var jsonData = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
          defval: null,
        });
        self.excelData[sourceKey] = {
          fileName: sourceKey + ".xlsx",
          data: jsonData,
          rowCount: jsonData.length,
        };
      } catch (error) {
        console.error("Error loading file from server for " + sourceKey + ":", error);
      }
    },
    cleanLabel(label, source) {
      if (!label) return label;
      var labelStr = String(label);
      if (source === "audienceBrand" || source === "audiencePrint") {
        var pattern = /^Brand\s*-\s*\d+\s*jours\s*/i;
        labelStr = labelStr.replace(pattern, "");
      }
      labelStr = labelStr.replace(/\s*\([^)]*\)/g, "");
      return labelStr.trim();
    },
    excelSerialToFrenchMonth(value) {
      if (typeof value === "number") {
        var excelEpoch = new Date(1899, 11, 30);
        var date = new Date(excelEpoch.getTime() + value * 86400000);
        var monthNames = [
          "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
          "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
        ];
        var month = monthNames[date.getMonth()];
        var year = date.getFullYear();
        return month + " " + year;
      }
      if (typeof value === "string") {
        var monthMap = {
          "janv": "Janvier", "jan": "Janvier",
          "févr": "Février", "fév": "Février", "feb": "Février",
          "mars": "Mars", "mar": "Mars",
          "avr": "Avril", "avril": "Avril",
          "mai": "Mai",
          "juin": "Juin",
          "juil": "Juillet", "juill": "Juillet",
          "août": "Août", "aout": "Août",
          "sept": "Septembre", "sep": "Septembre",
          "oct": "Octobre",
          "nov": "Novembre",
          "déc": "Décembre", "dec": "Décembre"
        };
        var match = value.toLowerCase().match(/([a-zéû]+)\.?\s*-?\s*(\d{2,4})/);
        if (match) {
          var monthAbbr = match[1];
          var yearStr = match[2];
          if (yearStr.length === 2) {
            yearStr = "20" + yearStr;
          }
          for (var key in monthMap) {
            if (monthAbbr.indexOf(key) !== -1 || key.indexOf(monthAbbr) !== -1) {
              return monthMap[key] + " " + yearStr;
            }
          }
        }
      }
      return String(value);
    },
    async processData() {
      var self = this;
      self.processing = true;
      self.showSnackbar("Processing Excel data...", "info", 5000);
      try {
        var labelsBySource = {};
        Object.keys(self.excelData).forEach(function(source) {
          var data = self.excelData[source];
          if (!data) return;
          var config = self.columnMapping[source];
          if (!config) return;
          var labelsSet = {};
          for (var i = config.dataStartRow; i < data.data.length; i++) {
            var row = data.data[i];
            if (!row) continue;
            var originalLabel = row[config.labelColumn];
            if (!originalLabel || originalLabel === "" || originalLabel === "-") continue;
            var cleanedLabel = self.cleanLabel(originalLabel, source);
            var labelKey = JSON.stringify({
              original: String(originalLabel),
              cleaned: cleanedLabel,
            });
            labelsSet[labelKey] = true;
          }
          labelsBySource[source] = Object.keys(labelsSet).map(function(l) {
            return JSON.parse(l);
          });
        });
        var rules = [];
        Object.keys(labelsBySource).forEach(function(source) {
          labelsBySource[source].forEach(function(labelInfo) {
            rules.push({
              source: source,
              excel_label: labelInfo.original,
              cleaned_label: labelInfo.cleaned,
            });
          });
        });
        if (rules.length > 0) {
          await axios.post("/api/marketing/matching-rules/bulk", rules);
          var excelRows = self.buildExcelRows();
          if (excelRows.length > 0) {
            var reportsResponse = await axios.post("/api/marketing/brand-reports/process", { rows: excelRows });
            await self.loadData();
            var processedCount = reportsResponse.data.processed || 0;
            self.showSnackbar("Données traitées — " + processedCount + " marque" + (processedCount !== 1 ? "s" : "") + " mise" + (processedCount !== 1 ? "s" : "") + " à jour");
          }
        } else {
          self.showSnackbar("Aucun fichier Excel trouvé. Importez d'abord les fichiers.", "warning");
        }
      } catch (error) {
        console.error("Error processing Excel data:", error);
        self.showSnackbar("Erreur lors du traitement des données", "error");
      } finally {
        self.processing = false;
      }
    },
    buildExcelRows() {
      var self = this;
      var rows = [];
      Object.keys(self.excelData).forEach(function(source) {
        var data = self.excelData[source];
        if (!data) return;
        var config = self.columnMapping[source];
        if (!config) return;
        var headerRow = config.headerRow >= 0 ? data.data[config.headerRow] : null;
        for (var i = config.dataStartRow; i < data.data.length; i++) {
          var row = data.data[i];
          if (!row) continue;
          var originalLabel = row[config.labelColumn];
          if (!originalLabel || originalLabel === "" || originalLabel === "-") continue;
          var values = config.valueColumns.map(function(colIdx) {
            var val = row[colIdx];
            if (val === null || val === undefined || val === "" || val === "ND" || val === "-") {
              return null;
            }
            return typeof val === "number" ? val : parseFloat(val) || null;
          });
          var headerValues = null;
          if (headerRow && source === "audienceDigit") {
            headerValues = config.valueColumns.map(function(colIdx) {
              var headerVal = headerRow[colIdx];
              if (headerVal) {
                return self.excelSerialToFrenchMonth(headerVal);
              }
              return null;
            });
          }
          rows.push({
            source: source,
            excel_label: String(originalLabel),
            values: values,
            header_values: headerValues,
          });
        }
      });
      return rows;
    },
  },
};
</script>

<style scoped>
.mid-help__section + .mid-help__section {
  margin-top: 20px;
}

.mid-help__section--warning {
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
}

.mid-help__title {
  font-size: 12px;
  font-weight: 800;
  color: #4338ca;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 8px;
}

.mid-help__title--warning {
  color: #c2410c;
}

.mid-help__list {
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
}

.mid-help__list li + li {
  margin-top: 4px;
}
</style>
